export type Success<T> = {
  fetchStatus: 'SUCCESS';
  data: T;
};

export type Refreshing<T> = {
  fetchStatus: 'REFRESHING';
  data: T;
};

export type Initial = {
  fetchStatus: 'INITIAL';
};

export type Loading = {
  fetchStatus: 'LOADING';
};

export type FetchError = {
  fetchStatus: 'ERROR';
  errorMessage: string;
};

export type Remote<T> =
  | Initial
  | Loading
  | FetchError
  | Success<T>
  | Refreshing<T>;

export type FetchStatus =
  | 'INITIAL'
  | 'LOADING'
  | 'REFRESHING'
  | 'SUCCESS'
  | 'ERROR';

export function resolve<T, R>(
  remote: Remote<T>,
  resolvers: {
    INITIAL?: () => R;
    LOADING: () => R;
    SUCCESS: (data: T) => R;
    REFRESHING?: (data: T) => R;
    ERROR: (errorMessage: string) => R;
  },
): R {
  if (remote.fetchStatus === 'ERROR') {
    return resolvers.ERROR(remote.errorMessage);
  }
  if (remote.fetchStatus === 'SUCCESS') {
    return resolvers.SUCCESS(remote.data);
  }
  if (remote.fetchStatus === 'LOADING') {
    return resolvers.LOADING();
  }
  if (remote.fetchStatus === 'REFRESHING') {
    return resolvers.REFRESHING
      ? resolvers.REFRESHING(remote.data)
      : resolvers.SUCCESS(remote.data);
  }

  return resolvers.INITIAL ? resolvers.INITIAL() : resolvers.LOADING();
}

export function isInitial(remote: Remote<unknown>): remote is Initial {
  return remote.fetchStatus === 'INITIAL';
}

export function isLoading(
  remote: Remote<unknown>,
): remote is Initial | Loading {
  return remote.fetchStatus === 'INITIAL' || remote.fetchStatus === 'LOADING';
}

export function isLoaded(remote: Remote<unknown>) {
  return !isLoading(remote);
}

export function isSuccess<T>(remote: Remote<T>): remote is Success<T> {
  return remote.fetchStatus === 'SUCCESS';
}

export function hasValue<T>(
  remote: Remote<T>,
): remote is Success<T> | Refreshing<T> {
  return (
    remote.fetchStatus === 'SUCCESS' || remote.fetchStatus === 'REFRESHING'
  );
}

export function isError(remote: Remote<unknown>): remote is FetchError {
  return remote.fetchStatus === 'ERROR';
}

export function map<R, T>(
  remote: Remote<R>,
  mapper: (value: R) => T,
): Remote<T> {
  if (hasValue(remote)) {
    return {
      ...remote,
      data: mapper(remote.data),
    };
  }

  return remote;
}

export function extractArray<R>(
  remote: Remote<R[]>,
  defaultSize = 1,
): Remote<R>[] {
  if (hasValue(remote)) {
    return remote.data.map((item) => ({
      ...remote,
      data: item,
    }));
  }

  return Array(defaultSize).fill(remote);
}

export function mapAtomValue<R>(mapper: (value: R) => R) {
  return (remote: Remote<R>) => map(remote, mapper);
}

export function getOptional<T>(remote: Remote<T>): T | undefined {
  return hasValue(remote) ? remote.data : undefined;
}
